import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useState } from "react";

import { NoxusInput } from "@/components/Form/InputField";

import { Plus, Trash2, GripVertical } from "lucide-react";

import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Button } from "../shadcn/button";

function SortableItem({
  id,
  value,
  onDelete,
  isDisabled,
}: {
  id: string;
  value: string;
  onDelete: (value: string) => void;
  isDisabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full">
      <StringArrayEntryItem
        value={value}
        onDelete={onDelete}
        isDisabled={isDisabled}
        dragAttributes={attributes}
        dragListeners={listeners}
      />
    </div>
  );
}

interface StringArrayInputProps {
  title?: string;
  value: string[];
  tooltip?: string;
  onChange: (value: string[]) => void;
  name?: string;
  label?: string;
  onDeleteEntry?: (value: string) => void;
  isDisabled?: boolean;
}

export const StringArrayInput = (props: StringArrayInputProps) => {
  const { value, onChange, onDeleteEntry, isDisabled } = props;
  const [inputValue, setInputValue] = useState("");
  const [isShaking, setIsShaking] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDeleteWebsite = (entry: string) => {
    if (isDisabled) return;
    props.onChange(value.filter((w) => w !== entry));
    onDeleteEntry?.(entry);
  };

  const handleAddEntry = () => {
    if (isDisabled) return;
    const trimmed = inputValue.trim();
    const notAddedYet = !value.some((website) => website === trimmed);

    if (trimmed && notAddedYet) {
      onChange([...value, trimmed]);
      setInputValue("");
    } else if (trimmed && !notAddedYet) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = value.findIndex((v) => v === active.id);
      const newIndex = value.findIndex((v) => v === over.id);
      const newValue = arrayMove(value, oldIndex, newIndex);
      onChange(newValue);
    }
  };

  return (
    <div className="space-y-3">
      {props.label && <NoxusFormLabel label={props.label} name={props.name} />}

      {value.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={value} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {value.map((v) => (
                <SortableItem
                  key={v}
                  id={v}
                  value={v}
                  onDelete={handleDeleteWebsite}
                  isDisabled={isDisabled}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="w-full gap-2 flex items-center">
        <div className="flex-1">
          <NoxusInput
            placeholder="Add new value"
            onChange={(event) => {
              setInputValue(event.target.value);
            }}
            value={inputValue}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                handleAddEntry();
              }
            }}
            disable={isDisabled}
          />
        </div>
        <div className={isShaking ? "animate-shake" : ""}>
          <NoxusIconButton
            icon={Plus}
            isDisabled={!inputValue || inputValue.length === 0}
            onClick={() => {
              handleAddEntry();
            }}
            disabled={isDisabled}
          />
        </div>
      </div>
    </div>
  );
};

interface IStringArrayField {
  name: string;
  tooltip?: string;
  label?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  defaultValue?: string[];
  isDisabled?: boolean;
}

export const StringArrayField = (props: IStringArrayField) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <StringArrayInput
            name={props.name}
            value={value || []}
            title={props.label} // this value will be used on the Add more button
            tooltip={props.tooltip}
            onChange={onChange}
            isDisabled={props.isDisabled}
          />
        );
      }}
    />
  );
};

const StringArrayEntryItem = ({
  value,
  onDelete,
  isDisabled,
  dragAttributes,
  dragListeners,
}: {
  value: string;
  onDelete: (value: string) => void;
  isDisabled?: boolean;
  dragAttributes?: any;
  dragListeners?: any;
}) => {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <NoxusInput value={value} disable={true} onChange={() => {}} />
      </div>
      <Button
        onClick={() => {
          onDelete(value);
        }}
        disabled={isDisabled}
        variant="outline"
      >
        <Trash2 />
      </Button>
      <Button
        className="cursor-grab active:cursor-grabbing"
        variant="ghost"
        size="icon"
        disabled={isDisabled}
        {...dragAttributes}
        {...dragListeners}
      >
        <GripVertical className="h-5 w-5" />
      </Button>
    </div>
  );
};
